"use client";

import { useTenantRouteContext } from "@/components/app-shell/TenantRouteContext";
// This import deliberately points at the local copied prototype inside
// `src/components`, not the original sibling repo. We moved away from
// cross-project aliases because Turbopack would panic when CSS/Tailwind
// traversed outside the app root.
import PSRApp from "@/components/PSRApp";
import { fetchPracticeByClinicId } from "@/lib/api/fetch-practice";
import { useClaritySession } from "@/lib/auth/auth-store";
import { setPracticeProperties } from "@/lib/analytics";
import { usePracticeChannel } from "@/lib/signalr/usePracticeChannel";
import { reportError } from "@/lib/observability/reportError";
import { DEFAULT_ERROR_MESSAGE } from "@/lib/request/extractApiErrorMessage";
import { toast } from "@/lib/toast";
import { ReactNode, useEffect } from "react";

export function PracticeWorkspace({ children }: { children?: ReactNode }) {
  const { tenantId } = useTenantRouteContext();
  usePracticeChannel(tenantId);
  const session = useClaritySession();

  useEffect(() => {
    if (session.status !== "authenticated" || !session.accessToken) {
      return;
    }

    const accessToken = session.accessToken;
    let isCancelled = false;

    async function verifyClinicToken() {
      try {
        const practice = await fetchPracticeByClinicId(tenantId, accessToken);

        if (isCancelled) {
          return;
        }

        setPracticeProperties(practice);
      } catch (error) {
        if (isCancelled) {
          return;
        }

        reportError(error, { source: "PracticeWorkspace", tenantId });
        toast.error(DEFAULT_ERROR_MESSAGE);
      }
    }

    void verifyClinicToken();

    return () => {
      isCancelled = true;
    };
  }, [session.accessToken, session.status, tenantId]);

  return (
    <div className="bg-surface-page text-content-primary relative h-screen overflow-hidden">
      <PSRApp key={tenantId}>{children}</PSRApp>
    </div>
  );
}
